"use client";

import { useEffect, useState } from "react";
import { getPrice } from "@/components/client/ProductDetail/api";
import { buildPriceListEntry } from "@/lib/pricing/buildPriceListEntry";
import type { EcommercePromoCode } from "@/lib/pricing/findPromoMatch";
import { extractProductEntryIdFromUrn } from "@/lib/pricing/extractProductEntryId";
import { toContentfulLocale } from "@/lib/locale/toContentfulLocale";
import { getCurrencyCodesNeedingApiPrice, type ProductPriceList } from "@/lib/pricing/buildContentfulPriceList";
export type { ProductPriceList } from "@/lib/pricing/buildContentfulPriceList";
type LinkedProductInfo = {
  sku: string;
  promoCodes: EcommercePromoCode[];
  currencyCodes: string[];
};
const collectPricingCards = (value: any): any[] => {
  const directCards = value?.pricingCardsCollection?.items ?? [];
  const tabCards = value?.pricingCardsInTabsCollection?.items?.flatMap((tab: any) => tab?.cardsCollection?.items ?? []) ?? [];
  return [...directCards, ...tabCards].filter(Boolean);
};
export function useLayoutPricingCardPrices(value: any, currency?: string, countryCode?: string, lang?: string) {
  const [productPriceLists, setProductPriceLists] = useState<Record<string, ProductPriceList>>({});
  useEffect(() => {
    // Match PDP: only fetch once ecom has resolved currency + country.
    if (!currency || !countryCode || !value) {
      setProductPriceLists({});
      return;
    }
    const cards = collectPricingCards(value);
    const cardsNeedingApi = cards.map(card => {
      const cardId = card?.sys?.id;
      const entryId = extractProductEntryIdFromUrn(card?.product?.sys?.urn);
      const currencyCodes = getCurrencyCodesNeedingApiPrice(card, value?.currencyList);
      return {
        card,
        cardId,
        entryId,
        currencyCodes
      };
    }).filter((plan): plan is typeof plan & {
      cardId: string;
      entryId: string;
    } => Boolean(plan.card?.product?.sys?.urn && plan.cardId && plan.entryId && plan.currencyCodes.length > 0));
    if (!cardsNeedingApi.length) {
      setProductPriceLists({});
      return;
    }
    let cancelled = false;
    const locale = toContentfulLocale(lang ?? "en");
    const loadPrices = async () => {
      const productByCardId = new Map<string, LinkedProductInfo>();
      await Promise.all(cardsNeedingApi.map(async ({
        cardId,
        entryId,
        currencyCodes
      }) => {
        try {
          const response = await fetch(`/api/product-information?id=${encodeURIComponent(entryId)}&locale=${encodeURIComponent(locale)}`);
          const data = await response.json();
          if (data?.sku) {
            productByCardId.set(cardId, {
              sku: data.sku,
              promoCodes: data.promoCodes ?? [],
              currencyCodes
            });
          }
        } catch (error) {
          console.error("Failed to resolve pricing card product SKU", error);
        }
      }));
      const nextPriceLists: Record<string, ProductPriceList> = {};
      await Promise.all([...productByCardId.entries()].flatMap(([cardId, product]) => product.currencyCodes.map(async currencyCode => {
        const priceResponse = await getPrice(currencyCode, product.sku, countryCode, () => {});
        const entry = buildPriceListEntry(priceResponse, currencyCode, product.promoCodes);
        if (!entry) return;
        if (!nextPriceLists[cardId]) {
          nextPriceLists[cardId] = {};
        }
        nextPriceLists[cardId][currencyCode] = entry;
      })));
      for (const {
        cardId,
        currencyCodes
      } of cardsNeedingApi) {
        for (const currencyCode of currencyCodes) {
          if (!nextPriceLists[cardId]?.[currencyCode]) {
            if (!nextPriceLists[cardId]) nextPriceLists[cardId] = {};
            nextPriceLists[cardId][currencyCode] = {
              currency: "Pricing unavailable",
              crossedOutPrice: "",
              discount: null,
              discountCode: null
            };
          }
        }
      }
      if (!cancelled) {
        setProductPriceLists(nextPriceLists);
      }
    };
    loadPrices();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, countryCode, lang, JSON.stringify(value)]);
  return productPriceLists;
}